import { Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { AuthService, EntityService, SharedService } from '@app/core';
import { environment } from '@env/environment';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { HyperpureOrderService } from '../../../hyperpure/services/hyperpure-order.service';
import { CheckoutMethod, SupplierType } from '../../analytics/models/analytics.interfaces';
import { PurchaseManagerAnalyticsService } from '../../analytics/services/purchase-manager-analytics.service';
import { CartUpdateEvent, SupplierProduct, SupplierSection } from '../../models/supplier.models';
import { PriceDiscoveryService } from '../../services/price-discovery.service';

/**
 * Child component that encapsulates all Hyperpure-specific logic:
 * - Login & session management
 * - Cart fetch / local sync / session tracking
 * - Iframe management & communication
 * - Proceed-to-buy flow
 *
 * The parent (PmPriceDiscoveryComponent) delegates Hyperpure cart operations here.
 */
@Component({
    selector: 'app-hyperpure-supplier',
    templateUrl: './hyperpure-supplier.component.html',
    styleUrls: ['./hyperpure-supplier.component.scss']
})
export class HyperpureSupplierComponent implements OnInit, OnDestroy {

    // ── Inputs from parent ──
    @Input() entityId = '';
    @Input() storeUuid = '';
    @Input() supplierSection: SupplierSection | null = null;

    // ── Outputs to parent ──
    @Output() cartUpdated = new EventEmitter<CartUpdateEvent>();
    @Output() checkoutDrawerRequested = new EventEmitter<string>(); // platform
    @Output() iframeClosed = new EventEmitter<void>();

    // ── Hyperpure login data ──
    loginData: any = null;

    // ── Cart state ──
    apiCartCount = 0;
    apiCartProducts: any[] = [];
    apiCartItems: SupplierProduct[] = []; // Normalized items for checkout display
    private productNumbers = new Map<string, string>(); // sku_id → product_number

    // ── Session tracking ──
    sessionModifiedSkus = new Set<string>();
    private originalQuantities = new Map<string, number>();
    isSessionItemsCollapsed = false;
    isCartItemsCollapsed = true;

    // ── Iframe ──
    @ViewChild('iframeElement') iframeElement!: ElementRef;
    showIframe = false;
    isIframeExpanded = false;
    iframeUrl: string = 'https://www.hyperpure.com/in/Init';
    iframeUrlSafe: SafeResourceUrl | null = null;
    private trustedOrigin = environment.hyperpureTrustedOrigin;
    private listenerCalled = false;

    // -- Polling for Order Success --
    private pollingInterval: any;
    private lastOrderTime: string | null = null;

    private destroy$ = new Subject<void>();

    constructor(
        private sanitizer: DomSanitizer,
        private priceDiscoveryService: PriceDiscoveryService,
        private entityService: EntityService,
        private authService: AuthService,
        private sharedService: SharedService,
        private hyOrderService: HyperpureOrderService,
        private pmAnalytics: PurchaseManagerAnalyticsService
    ) { }

    ngOnInit(): void {
        this.setupMessageListener();
    }

    ngOnDestroy(): void {
        this.stopOrderPolling();
        this.destroy$.next();
        this.destroy$.complete();
    }

    // ── Public API for Parent ──

    /**
     * Called by parent after integration status is resolved.
     * Fetches login data, then fetches cart.
     */
    initialize(): void {
        if (this.storeUuid && this.entityId) {
            this.loadSessionModifiedSkus();
            this.fetchLoginData();
        }
    }

    /**
     * Called by parent after entity changes.
     */
    resetState(): void {
        this.loginData = null;
        this.apiCartCount = 0;
        this.apiCartProducts = [];
        this.apiCartItems = [];
        this.productNumbers.clear();
        this.clearSessionModifiedSkus();
        this.showIframe = false;
        this.isIframeExpanded = false;
        this.iframeUrlSafe = null;
        this.listenerCalled = false;
    }

    /**
     * Updates an item quantity and syncs with Hyperpure API.
     */
    updateQuantity(item: SupplierProduct, change: number, previousQuantity: number): void {
        // Optimistically sync local cart state immediately
        this.syncCartLocally(String(item.sku_id));

        item.isAddingToCart = true;

        const productId = Number(item.sku_id);
        const quantity = item.selectedQuantity || 0;
        const deliveryMode = 'DELIVERY';

        this.priceDiscoveryService.addToCart(productId, quantity, deliveryMode, this.entityId)
            .subscribe({
                next: (response) => {
                    console.log('✅ Hyperpure cart updated:', response);
                    item.isAddingToCart = false;
                    if (change > 0 && previousQuantity === 0) {
                        this.sharedService.successAlert(`Added "${item.item_name}" to cart`);
                    }
                },
                error: (error) => {
                    console.error('❌ Failed to update Hyperpure cart:', error);
                    item.isAddingToCart = false;
                    // Revert quantity on error
                    item.selectedQuantity = previousQuantity;
                    this.syncCartLocally(String(item.sku_id));
                    const errorMessage = error?.error?.message || error?.message || 'Failed to update cart. Please try again.';
                    item.cartError = errorMessage;
                    this.sharedService.errorAlert(errorMessage);
                }
            });
    }

    /**
     * Adds an item to the cart (first time add).
     */
    addToCart(item: SupplierProduct, supplierName: string, previousQuantity: number): void {
        // Optimistically sync local cart state immediately
        this.syncCartLocally(String(item.sku_id));

        item.isAddingToCart = true;

        const productId = Number(item.sku_id);
        const quantity = item.selectedQuantity || 1;
        const deliveryMode = 'DELIVERY';

        this.priceDiscoveryService.addToCart(productId, quantity, deliveryMode, this.entityId)
            .subscribe({
                next: (response) => {
                    console.log('✅ Hyperpure item added to cart:', response);
                    item.isAddingToCart = false;
                    this.sharedService.successAlert(`Added "${item.item_name}" from ${supplierName} to cart`);
                },
                error: (error) => {
                    console.error('❌ Failed to add Hyperpure item to cart:', error);
                    item.isAddingToCart = false;
                    item.selectedQuantity = previousQuantity;
                    this.syncCartLocally(String(item.sku_id));
                    const errorMessage = error?.error?.message || error?.message || 'Failed to add item to cart. Please try again.';
                    item.cartError = errorMessage;
                    this.sharedService.errorAlert(errorMessage);
                }
            });
    }

    /**
     * Proceed-to-buy flow: opens the Hyperpure bolt cart iframe.
     */
    proceedToBuy(): void {
        // Verify login data is available
        if (!this.loginData || !this.loginData.token) {
            console.error('❌ Hyperpure login data not available');
            this.sharedService.errorAlert('Please wait while we set up Hyperpure integration');
            return;
        }

        // Small delay for better UX
        setTimeout(() => {
            // Force iframe reload every time to ensure fresh content
            if (this.iframeUrl) {
                this.iframeUrlSafe = null;
                setTimeout(() => {
                    this.iframeUrlSafe = this.sanitizer.bypassSecurityTrustResourceUrl(this.iframeUrl);
                }, 10);
            }

            // Show the iframe
            this.showIframe = true;
            this.isIframeExpanded = true;
            document.body.style.overflow = 'hidden';

            // Wait for iframe to be visible, then send cart load message
            setTimeout(() => {
                this.sendCartLoadMessage();
            }, 1000);

            // Clear Hyperpure cart data from the supplier section
            if (this.supplierSection) {
                this.supplierSection.items.forEach(item => {
                    item.selectedQuantity = 0;
                });
            }

            this.sharedService.successAlert('Opening Hyperpure checkout');
            this.startOrderPolling();
        }, 500);
    }

    /**
     * Starts polling for new orders to track successful checkout.
     */
    private startOrderPolling(): void {
        this.stopOrderPolling(); // Clean up any existing poll

        // Record current time to ignore older orders
        this.lastOrderTime = new Date().toISOString();
        console.log('🔄 Starting Hyperpure order polling. Base time:', this.lastOrderTime);

        this.pollingInterval = setInterval(() => {
            this.hyOrderService.getOrders({
                limit: 5,
                entityId: this.entityId
            }).pipe(takeUntil(this.destroy$)).subscribe(res => {
                if (res.success && res.data && res.data.orders) {
                    const newOrder = res.data.orders.find(order => {
                        // Check if order was placed after we started polling
                        return new Date(order.createdAt) > new Date(this.lastOrderTime!);
                    });

                    if (newOrder) {
                        console.log('✅ New Hyperpure order detected via polling:', newOrder.orderNumber);
                        this.pmAnalytics.trackProceedToBuyResult(SupplierType.Hyperpure, true, {
                            checkoutMethod: CheckoutMethod.Iframe,
                            responseTimeMs: 0,
                            totalItemCount: newOrder.orderItems?.length || 0,
                            totalValue: newOrder.totalOrderedValue || 0
                        });
                        this.stopOrderPolling();
                        this.sharedService.successAlert('Order placed successfully!');
                    }
                }
            }, err => {
                console.error('Error polling Hyperpure orders:', err);
            });
        }, 10000); // Poll every 10 seconds
    }

    private stopOrderPolling(): void {
        if (this.pollingInterval) {
            clearInterval(this.pollingInterval);
            this.pollingInterval = null;
        }
    }

    /**
     * Returns session-modified items from a checkout cart.
     */
    getSessionModifiedItems(checkoutItems: SupplierProduct[]): SupplierProduct[] {
        return (checkoutItems || []).filter(
            (item: SupplierProduct) => this.sessionModifiedSkus.has(String(item.sku_id))
        );
    }

    /**
     * Returns non-session (pre-existing) items from a checkout cart.
     */
    getNonSessionCartItems(checkoutItems: SupplierProduct[]): SupplierProduct[] {
        return (checkoutItems || []).filter(
            (item: SupplierProduct) => !this.sessionModifiedSkus.has(String(item.sku_id))
        );
    }

    // ── Iframe Management ──

    closeIframe(): void {
        this.showIframe = false;
        this.isIframeExpanded = false;
        this.iframeUrlSafe = null;
        document.body.style.overflow = '';

        // Re-sync cart from Hyperpure API (user may have added/removed/changed items in iframe)
        this.fetchCart();
        this.iframeClosed.emit();
    }

    toggleIframeExpansion(): void {
        this.isIframeExpanded = !this.isIframeExpanded;
        if (this.isIframeExpanded) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'auto';
        }
    }

    closeIframePanel(): void {
        this.showIframe = false;
        this.isIframeExpanded = false;
        document.body.style.overflow = 'auto';

        // Re-sync cart from Hyperpure API
        this.fetchCart();
        this.iframeClosed.emit();
    }

    reloadIframe(): void {
        if (this.iframeUrl) {
            this.iframeUrlSafe = null;
            setTimeout(() => {
                this.iframeUrlSafe = this.sanitizer.bypassSecurityTrustResourceUrl(this.iframeUrl);
            }, 10);
        }
    }

    // ── Hyperpure Login ──

    private fetchLoginData(): void {
        if (!this.entityId) return;

        const selectedEntity = this.entityService.getEntities()?.find((e: any) => e.id === this.entityId);
        if (!selectedEntity) return;

        if (!this.storeUuid) return;

        const currentUser = this.authService.user;
        const email = `purchase_${this.entityId}@petpooja.com`;
        const phone = currentUser?.mobile || selectedEntity.contact || '';

        const requestPayload = {
            store_uuid: this.storeUuid,
            store_details: [],
            user_details: {}
        };

        this.priceDiscoveryService.getHyperpureLoginData(this.entityId, requestPayload)
            .pipe(takeUntil(this.destroy$))
            .subscribe(
                (data: any) => {
                    console.log('✅ Hyperpure sign-in successful:', data);

                    const storeDetailsArray = [{
                        store_id: data.hyperpureIdMd5,
                        restaurant_name: data.storeName || '',
                        restaurant_gstno: data.storeGst || '',
                        store_address: data.storeAddress || '',
                        store_pincode: data.storePincode || ''
                    }];
                    const all_store_details = JSON.stringify(storeDetailsArray);

                    const allStoreObj = {
                        store_id: data.store_id || data.storeId,
                        email: data.email || email,
                        phone: data.phone || phone,
                        all_store_details: all_store_details
                    };

                    this.loginData = {
                        email: data.email || email,
                        phone: data.phone || phone,
                        store_id: data.store_id || data.storeId,
                        token: data.token,
                        store_uuid: data.store_uuid || data.storeUuid || this.storeUuid,
                        ActionData: {},
                        checksum: '',
                        all_store_obj: allStoreObj,
                        session_id: data.session_id || data.sessionId
                    };

                    // Fetch current cart
                    this.fetchCart();

                    // Load iframe AFTER sign-in
                    this.loadIframe();
                },
                (error: any) => {
                    console.error('Failed to sign-in to Hyperpure:', error);
                    this.sharedService.errorAlert('Failed to sign-in to Hyperpure');
                    this.loginData = {
                        token: '',
                        store_uuid: this.storeUuid,
                        entity_id: this.entityId
                    };
                }
            );
    }

    // ── Cart Operations ──

    fetchCart(): void {
        if (!this.storeUuid || !this.entityId) return;

        this.priceDiscoveryService.getCart(this.storeUuid, 'DELIVERY', this.entityId)
            .pipe(takeUntil(this.destroy$))
            .subscribe(
                (response: any) => {
                    const cartProducts = response?.response?.cartProducts || response?.response?.cart_products || [];
                    const cartCount = response?.response?.cartCount || response?.response?.cart_count || cartProducts.length;

                    // If cart is empty, clear session
                    if (cartCount === 0 || cartProducts.length === 0) {
                        this.clearSessionModifiedSkus();
                    }

                    this.apiCartCount = cartCount;
                    this.apiCartProducts = cartProducts;

                    // Build normalized items
                    this.apiCartItems = cartProducts.map((cp: any) => {
                        const pd = cp.product_details || cp.productDetails || {};
                        return {
                            item_name: pd.product_name || pd.productName || '',
                            sku_id: String(pd.product_id || pd.productId || ''),
                            rate: Number(pd.price_per_unit || pd.pricePerUnit || pd.discounted_price_per_unit || pd.discountedPricePerUnit || 0),
                            mrp: 0,
                            price: 0,
                            unit: pd.uom || pd.sub_uom || pd.subUom || 'unit',
                            quantity: cp.quantity || 1,
                            image_url: '',
                            product_images: '',
                            platform: 'hyperpure',
                            isBestMatch: false,
                            is_in_stock: pd.is_in_stock || false,
                            bulkPrices: [],
                            selectedQuantity: cp.quantity || 1,
                            cart_price_per_unit: pd.cart_price_per_unit || pd.cartPricePerUnit || ''
                        } as SupplierProduct;
                    });

                    // Save to localStorage
                    try {
                        const cartData = cartProducts.map((cp: any) => ({
                            product_id: cp.productDetails?.productId || cp.product_details?.product_id,
                            product_number: cp.productDetails?.productNumber || cp.product_details?.product_number,
                            product_name: cp.productDetails?.productName || cp.product_details?.product_name,
                            quantity: cp.quantity
                        }));
                        localStorage.setItem('hyperpureCart', JSON.stringify(cartData));
                    } catch (error) {
                        console.error('Failed to save Hyperpure cart to localStorage:', error);
                    }

                    // Snapshot original quantities
                    this.apiCartItems.forEach((item: SupplierProduct) => {
                        const skuId = String(item.sku_id);
                        if (!this.originalQuantities.has(skuId)) {
                            this.originalQuantities.set(skuId, item.selectedQuantity || 0);
                        }
                    });
                    this.saveSessionModifiedSkus();

                    // Build product_number lookup map
                    this.productNumbers.clear();
                    cartProducts.forEach((cp: any) => {
                        const pd = cp.product_details || cp.productDetails || {};
                        const productId = String(pd.product_id || pd.productId || '');
                        const productNumber = String(pd.product_number || pd.productNumber || '');
                        if (productId && productNumber) {
                            this.productNumbers.set(productId, productNumber);
                        }
                    });

                    // Build a map of product_id -> quantity from API response
                    const cartMap = new Map<string, number>();
                    cartProducts.forEach((cp: any) => {
                        const productId = String(cp.productDetails?.productId || cp.productDetails?.product_id || cp.product_details?.product_id || '');
                        if (productId) {
                            cartMap.set(productId, cp.quantity || 0);
                        }
                    });

                    // Update selectedQuantity on matching items in the supplier section
                    if (this.supplierSection) {
                        this.supplierSection.items.forEach(item => {
                            const skuId = String(item.sku_id || '');
                            if (cartMap.has(skuId)) {
                                item.selectedQuantity = cartMap.get(skuId) || 0;
                            } else {
                                item.selectedQuantity = 0;
                            }
                        });
                    }

                    // Notify parent of cart update
                    this.emitCartUpdate();
                },
                (error: any) => {
                    console.error('❌ Failed to fetch cart:', error);
                }
            );
    }

    syncCartLocally(modifiedSkuId?: string): void {
        const searchItems = this.supplierSection?.items || [];

        const activeSkuIds = new Set<string>();
        searchItems.forEach(item => {
            const skuStr = String(item.sku_id);
            if (item.selectedQuantity && item.selectedQuantity > 0) {
                activeSkuIds.add(skuStr);
            }
        });
        if (modifiedSkuId) {
            activeSkuIds.add(String(modifiedSkuId));
        }

        const unchangedApiItems = this.apiCartItems.filter(
            ai => !activeSkuIds.has(String(ai.sku_id))
        );

        const searchItemsInCart = searchItems.filter(
            item => item.selectedQuantity && item.selectedQuantity > 0
        );

        this.apiCartItems = [
            ...unchangedApiItems,
            ...searchItemsInCart.map(item => ({
                item_name: item.item_name,
                sku_id: item.sku_id,
                rate: item.rate,
                baseRate: item.baseRate || item.rate,
                mrp: item.mrp || 0,
                price: item.price || 0,
                unit: item.unit,
                quantity: item.selectedQuantity || 1,
                image_url: item.image_url || '',
                product_images: item.product_images || '',
                platform: 'hyperpure',
                isBestMatch: item.isBestMatch || false,
                is_in_stock: item.is_in_stock !== false,
                bulkPrices: item.bulkPrices || [],
                selectedQuantity: item.selectedQuantity || 1,
                minimumOrderQty: item.minimumOrderQty,
                maximumOrderQty: item.maximumOrderQty,
                avlOrderQty: item.avlOrderQty
            } as SupplierProduct))
        ];

        this.apiCartCount = this.apiCartItems.length;

        // Save to localStorage
        try {
            const cartData = this.apiCartItems.map(item => ({
                product_id: item.sku_id,
                product_number: this.productNumbers.get(String(item.sku_id)) || '',
                product_name: item.item_name,
                quantity: item.selectedQuantity
            }));
            localStorage.setItem('hyperpureCart', JSON.stringify(cartData));
        } catch (error) {
            console.error('Failed to save cart to localStorage:', error);
        }

        // Track the modified SKU
        if (modifiedSkuId) {
            const modifiedItem = this.apiCartItems.find(i => String(i.sku_id) === modifiedSkuId);
            const currentQty = modifiedItem ? (modifiedItem.selectedQuantity || 0) : 0;
            this.trackSessionModification(modifiedSkuId, currentQty);
        }

        // Notify parent of cart update
        this.emitCartUpdate();
    }

    // ── Session Tracking ──

    private trackSessionModification(skuId: string, currentQty: number): void {
        const originalQty = this.originalQuantities.get(skuId) ?? 0;
        if (currentQty !== originalQty) {
            this.sessionModifiedSkus.add(skuId);
        } else {
            this.sessionModifiedSkus.delete(skuId);
        }
        this.saveSessionModifiedSkus();
    }

    private loadSessionModifiedSkus(): void {
        try {
            const stored = localStorage.getItem('hyperpureSessionItems');
            if (stored) {
                const data = JSON.parse(stored);
                if (data && typeof data === 'object' && !Array.isArray(data)) {
                    this.sessionModifiedSkus = new Set(data.skus || []);
                    this.originalQuantities = new Map(
                        Object.entries(data.originals || {}).map(([k, v]) => [k, Number(v)])
                    );
                } else if (Array.isArray(data)) {
                    this.sessionModifiedSkus = new Set(data);
                    this.originalQuantities = new Map();
                }
            }
        } catch (e) {
            this.sessionModifiedSkus = new Set();
            this.originalQuantities = new Map();
        }
    }

    private saveSessionModifiedSkus(): void {
        try {
            const originals: { [key: string]: number } = {};
            this.originalQuantities.forEach((v, k) => originals[k] = v);
            const data = {
                skus: [...this.sessionModifiedSkus],
                originals
            };
            localStorage.setItem('hyperpureSessionItems', JSON.stringify(data));
        } catch (e) {
            console.error('Failed to save session items:', e);
        }
    }

    clearSessionModifiedSkus(): void {
        this.sessionModifiedSkus.clear();
        this.originalQuantities.clear();
        localStorage.removeItem('hyperpureSessionItems');
    }

    // ── Iframe Management & Communication ──

    private setupMessageListener(): void {
        window.addEventListener('message', (event: MessageEvent) => {
            const isHyperpureOrigin = event.origin === this.trustedOrigin;
            const isOwnOrigin = event.origin === window.location.origin;

            if (!isHyperpureOrigin && !isOwnOrigin) {
                return;
            }

            let response = event.data;
            console.log(response, "PP HP response");

            if (typeof response === 'string') {
                try {
                    response = JSON.parse(response);
                } catch (e) {
                    return;
                }
            }

            if (!this.isValidMessage(response)) {
                return;
            }

            const topic = response.topic || response.Topic;

            if (topic === 'hyperpure_frame_loaded') {
                this.invokeDispatcher();
            }

            if (topic === 'petpooja_store_registered') {
                if (this.listenerCalled) return;
                this.listenerCalled = true;
                this.handleStoreRegistration(response);
            }
        }, false);
    }

    private isValidMessage(data: any): boolean {
        return (
            typeof data === 'object' &&
            typeof data.Topic === 'string'
        );
    }

    private buildDispatcherData(): any {
        return {
            Topic: 'hyperpure_login',
            Data: this.loginData || {}
        };
    }

    private invokeDispatcher(): void {
        const tryDispatch = (attempts = 0): void => {
            try {
                if (!this.iframeElement) {
                    if (attempts < 10) {
                        setTimeout(() => tryDispatch(attempts + 1), 500);
                        return;
                    }
                    return;
                }

                const iframe = this.iframeElement.nativeElement;
                if (!iframe) return;

                if (!iframe.contentWindow) {
                    if (attempts < 10) {
                        setTimeout(() => tryDispatch(attempts + 1), 500);
                        return;
                    }
                    return;
                }

                const payload = this.buildDispatcherData();
                iframe.contentWindow.postMessage(JSON.stringify(payload), this.iframeUrl);

                // Trigger cart load after login
                setTimeout(() => {
                    this.sendCartLoadMessage();
                }, 1000);

            } catch (err) {
                if (attempts < 5) {
                    setTimeout(() => tryDispatch(attempts + 1), 1000);
                }
            }
        };

        tryDispatch();
    }

    private sendCartLoadMessage(): void {
        const tryDispatch = (attempts = 0): void => {
            try {
                if (!this.iframeElement) {
                    if (attempts < 10) {
                        setTimeout(() => tryDispatch(attempts + 1), 500);
                        return;
                    }
                    return;
                }

                const iframe = this.iframeElement.nativeElement;
                if (!iframe || !iframe.contentWindow) {
                    if (attempts < 10) {
                        setTimeout(() => tryDispatch(attempts + 1), 500);
                        return;
                    }
                    return;
                }

                const payload = {
                    Topic: 'hyperpure_frame_load_cart',
                    Data: this.loginData || {}
                };

                iframe.contentWindow.postMessage(JSON.stringify(payload), this.iframeUrl);
            } catch (err) {
                if (attempts < 5) {
                    setTimeout(() => tryDispatch(attempts + 1), 1000);
                }
            }
        };

        tryDispatch();
    }

    private handleStoreRegistration(response: any): void {
        this.priceDiscoveryService.saveHyperpureStoreRegistration(response)
            .pipe(takeUntil(this.destroy$))
            .subscribe(
                (result: any) => {
                    this.sharedService.successAlert('Hyperpure store registered successfully');
                    if (result.shouldRedirect) {
                        // Handle redirect if needed
                    }
                },
                (error: any) => {
                    console.error('Failed to save store registration:', error);
                    this.sharedService.errorAlert('Failed to save store registration');
                }
            );
    }

    private loadIframe(): void {
        if (this.iframeUrl) {
            this.iframeUrlSafe = this.sanitizer.bypassSecurityTrustResourceUrl(this.iframeUrl);

            setTimeout(() => {
                const iframe = this.iframeElement?.nativeElement;
                if (iframe) {
                    iframe.onload = () => {
                        setTimeout(() => {
                            this.invokeDispatcher();
                        }, 3000);
                    };

                    iframe.onerror = (error: any) => {
                        console.error('❌ Iframe failed to load:', error);
                    };
                }
            }, 500);
        }
    }

    private emitCartUpdate(): void {
        this.cartUpdated.emit({
            platform: 'hyperpure',
            cartCount: this.apiCartCount,
            cartItems: this.apiCartItems
        });
    }
}
